/*
 * Copyright (c) 2025-2026 Aegis AO Soft LLC and Alexander Orlov.
 * Confidential and proprietary. Author: Alexander Orlov.
 *
 * Phone page opened by scanning the QR on the desktop "Driver license" step. The visitor takes a
 * Front and Back photo with the phone camera; each upload is OCR'd server-side and PUSHED back to
 * the desktop over SSE (no polling). Standalone, full-screen, no site chrome.
 */

import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Camera, CheckCircle2, Loader2 } from 'lucide-react';
import api from '../services/api';

type SideState = 'idle' | 'uploading' | 'done' | 'error';

export default function MobileLicenseUpload() {
  const { sessionId = '' } = useParams();
  const { t } = useTranslation();
  const [alive, setAlive] = useState<boolean | null>(null);
  const [front, setFront] = useState<SideState>('idle');
  const [back, setBack] = useState<SideState>('idle');

  useEffect(() => {
    let active = true;
    fetch(api.mobileLicenseEventsUrl(sessionId).replace('/events', ''))
      .then((r) => { if (active) setAlive(r.ok); })
      .catch(() => { if (active) setAlive(false); });
    return () => { active = false; };
  }, [sessionId]);

  const upload = async (side: 'front' | 'back', file: File) => {
    const set = side === 'front' ? setFront : setBack;
    set('uploading');
    const ok = await api.uploadMobileLicensePhoto(sessionId, side, file);
    set(ok ? 'done' : 'error');
  };

  if (alive === false) {
    return (
      <Shell>
        <p className="text-center text-gray-600">
          {t('mobileLicense.expired', 'This link has expired. Please start again on your computer.')}
        </p>
      </Shell>
    );
  }

  return (
    <Shell>
      <h1 className="text-2xl font-bold text-gray-900 text-center">{t('mobileLicense.title', 'Driver license')}</h1>
      <p className="text-sm text-gray-500 text-center mt-1 mb-6">
        {t('mobileLicense.subtitle', 'Take a clear photo of each side. They appear on your computer automatically.')}
      </p>

      <CaptureTile label={t('auth.licenseFront', 'Front')} state={front} onPick={(f) => upload('front', f)} />
      <div className="h-4" />
      <CaptureTile label={t('auth.licenseBack', 'Back')} state={back} onPick={(f) => upload('back', f)} />

      {front === 'done' && back === 'done' && (
        <p className="mt-6 text-center text-green-600 font-medium">
          {t('mobileLicense.allDone', 'All set! Return to your computer to continue.')}
        </p>
      )}
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-white flex flex-col items-center justify-center px-5 py-8">
      <div className="w-full max-w-sm">{children}</div>
    </div>
  );
}

function CaptureTile({ label, state, onPick }: { label: string; state: SideState; onPick: (f: File) => void }) {
  const { t } = useTranslation();
  return (
    <label className="block border-2 border-dashed border-gray-300 rounded-xl p-6 text-center cursor-pointer active:border-yellow-500">
      <input
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) onPick(f); }}
      />
      {state === 'uploading' ? (
        <Loader2 className="mx-auto h-8 w-8 animate-spin text-yellow-600" />
      ) : state === 'done' ? (
        <CheckCircle2 className="mx-auto h-8 w-8 text-green-500" />
      ) : (
        <Camera className="mx-auto h-8 w-8 text-gray-400" />
      )}
      <div className="mt-2 font-semibold text-gray-800">{label}</div>
      <div className="text-xs text-gray-500 mt-0.5">
        {state === 'error'
          ? t('mobileLicense.retry', 'Upload failed — tap to retry')
          : state === 'done'
          ? t('mobileLicense.uploaded', 'Uploaded')
          : t('mobileLicense.tap', 'Tap to open camera')}
      </div>
    </label>
  );
}
